// The cache work looks of the World field `cacheWork` (R2833,
// ui-map-interface.md §6.1.14, themes.md §3.4).
//
// A cache work look is what an own fleet draws at its hull while it works
// a SEARCH (v2-the-deep.md §9.1, §9.2). It is one of six sea
// mine collect looks of round 073, played on the crates of a cache in place
// of sea mines (R2860): this module plans the pieces and maps the
// share of the work to the timeline of the look. The code of each look is
// `SEA_MINE_COLLECT_LOOKS` of `sapper-collect-looks.ts`, and nothing here
// copies it.
//
// The look takes the work in `rounds` takes of `pieces` pieces each. Each
// take plays the whole timeline of its card, and the stow display counts
// to `cap`, so the stow display is the share of the work done. On the
// map the look draws the wall seconds the work still runs below the hull,
// in place of the stow figure. A still
// look (reduced motion) draws the pieces of the current take where they
// lie and the stow of the takes before it.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.

import { CACHE_WORK_STYLES } from '../gen/balance';
import {
  COLLECT_SPAN, SEA_MINE_COLLECT_LOOKS, paintSeaMineCollect, planSeaMineTake,
  type CollectScene, type FieldMine, type Schedule, type StowItem, type TakePlan,
} from './sapper-collect-looks';
import { Fl, S, SEA_INK, lw, type Ctx } from './seaobject-kit';
import { clamp01 } from './math';
import { frac } from './recipe-kit';

/** One look of the `cacheWork` field. */
export type CacheWorkStyle = (typeof CACHE_WORK_STYLES)[number];

/** The figures of the look, `[presentation.seaobjects] cache_work_*`. */
export interface CacheWorkFigures {
  /** The takes of one work, and the pieces of one take. */
  rounds: number;
  pieces: number;
  /** The ring of the take, world px. */
  R: number;
  /** The figure the stow counts to. */
  cap: number;
}

/** One take of a work: its pieces, the plan, the schedule of the look,
 *  the stow before it and the look second it ends at. */
export interface CacheTake {
  round: number;
  field: readonly FieldMine[];
  P: TakePlan;
  Q: Schedule;
  stow0: readonly StowItem[];
  kEnd: number;
}

/** The turn between two pieces: the golden angle, so no two takes lay
 *  their pieces on one bearing. */
const GOLDEN = Math.PI * (3 - Math.sqrt(5));

/** The inner and the outer share of the ring a piece lies in. */
const PIECE_IN = 0.4, PIECE_SPAN = 0.5;

/** The fill of the stow after the last arrival, look seconds: the
 *  `stowState` of the collect look fills an item in 0.22 s. */
const STOW_FILL_SEC = 0.3;

/** The cargo of one piece: the cap shared by every piece of the work. */
function pieceCargo(f: CacheWorkFigures): number {
  return f.cap / Math.max(1, f.rounds * f.pieces);
}

/**
 * The take `round` of the work of the fleet `seed` in the look `style`:
 * the pieces of the take round the hull, nearest first, every one aboard.
 * The pieces of the takes before stand in the stow.
 */
export function cacheTake(style: CacheWorkStyle, round: number, seed: number, f: CacheWorkFigures): CacheTake {
  const cargo = pieceCargo(f), field: FieldMine[] = [];
  const turn = (seed % 360) * (Math.PI / 180);
  for (let j = 0; j < f.pieces; j++) {
    const i = round * f.pieces + j;
    const a = turn + i * GOLDEN;
    const d = f.R * (PIECE_IN + PIECE_SPAN * frac(i * 0.618034 + seed * 0.137));
    field.push({ id: i + 1, kind: 'barb', side: 'own', d, a, x: Math.cos(a) * d, y: Math.sin(a) * d, cargo });
  }
  const stow0: StowItem[] = [];
  for (let i = 0; i < round * f.pieces; i++) stow0.push({ kind: 'barb', cargo });
  // The plan takes a piece while the stow has room; the slack keeps the
  // last piece aboard against the rounding of the shared cargo.
  const P = planSeaMineTake(field, f.R, round * f.pieces * cargo, f.cap + 1e-6);
  const Q = SEA_MINE_COLLECT_LOOKS[style].prep(P, f.R);
  let last = 0;
  for (const e of P.takes) last = Math.max(last, Q.arr.get(e.m.id) ?? 0);
  return { round, field, P, Q, stow0, kEnd: Math.max(COLLECT_SPAN, last + STOW_FILL_SEC) };
}

/** The take a share of the work is in, and the share of that take done. */
export function cacheWorkAt(share: number, f: CacheWorkFigures): { round: number; u: number } {
  const n = Math.max(1, f.rounds), s = clamp01(share) * n;
  const round = Math.min(n - 1, Math.floor(s));
  return { round, u: clamp01(s - round) };
}

/** Everything the paint reads besides the take. */
export interface CacheWorkPaint {
  /** The share of the take done, 0 to 1. */
  u: number;
  /** The look holds still: reduced motion. */
  still: boolean;
  ownCol: number;
  z: number;
  face: string;
  /** The heading of the hull, as `CollectScene.head` (R2711). */
  head: number;
  cap: number;
  /** The wall seconds the work still runs. The look then draws this timer
   *  below the hull in place of the stow figure. None keeps the figure. */
  timer?: number;
}

/**
 * The mark of one piece: a crate of a cache, in the own ink with the pale
 * rim of the water marks. A SEARCH draws crates alone: the owner ruled the
 * shard of a wreck away (R2860).
 */
function paintPiece(c: Ctx, x: number, y: number, ink: number, a: number, s: number, z: number): void {
  if (!(a > 0)) return;
  const h = 2.6 * s;
  c.beginPath(); c.rect(x - h, y - h, 2 * h, 2 * h);
  Fl(c, ink, 0.85 * a); S(c, SEA_INK.pale, 0.8 * a, lw(0.8, z));
}

/**
 * Paints one take of a cache work in one look, in the frame of the hull at
 * the origin, bow to +x: the take at the share `u` of its timeline, and the
 * stow display of the look.
 */
export function paintCacheWork(c: Ctx, style: CacheWorkStyle, take: CacheTake, R: number, o: CacheWorkPaint): void {
  const X: CollectScene = {
    k: o.still ? -1 : clamp01(o.u) * take.kEnd,
    R, P: take.P, Q: take.Q, stow0: take.stow0, cap: o.cap,
    ownCol: o.ownCol, foeCol: o.ownCol, z: o.z, face: o.face, head: o.head, timer: o.timer,
    mark: (cc, _m, x, y, _q, a, s) => paintPiece(cc, x, y, o.ownCol, a, s, o.z),
  };
  paintSeaMineCollect(c, style, X);
}
